import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { ArrowLeft, Clock, Eye, Tag, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      fetchPost(slug);
    } else {
      fetchPosts();
    }
  }, [slug]);

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/blog/posts`);
      setPosts(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPost = async (s) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/blog/posts/${s}`);
      setSelectedPost(data);
    } catch (err) {
      console.error(err);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent animate-spin"></div>
      </div>
    );
  }

  // Single Post View
  if (selectedPost) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0EE]">
        <nav className="border-b border-[#222] bg-[#0A0A0A]/90 backdrop-blur-md sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
            <button onClick={() => navigate('/')} className="text-xl font-black tracking-[-0.05em]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              VAULT
            </button>
            <div className="flex gap-4 text-sm text-[#888]">
              <button onClick={() => navigate('/blog')} className="hover:text-white transition-colors">Blog</button>
              <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Sign In</button>
            </div>
          </div>
        </nav>

        <article className="max-w-3xl mx-auto px-6 py-16" data-testid="blog-post">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-[#888] hover:text-white mb-8 transition-colors text-sm"
          >
            <ArrowLeft size={16} /> Back to Blog
          </button>

          <h1 className="text-3xl sm:text-5xl font-black leading-tight tracking-[-0.03em] mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {selectedPost.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-[#888] mb-10 pb-8 border-b border-[#222]">
            <span className="flex items-center gap-1.5">
              <Clock size={14} />
              {selectedPost.created_at ? new Date(selectedPost.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
            </span>
            <span className="flex items-center gap-1.5">
              <Eye size={14} /> {selectedPost.views || 0} views
            </span>
            {selectedPost.author_name && <span>By {selectedPost.author_name}</span>}
          </div>

          <div
            className="prose-vault"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedPost.content) }}
          />

          {selectedPost.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-[#222]">
              {selectedPost.tags.map((t, i) => (
                <span key={i} className="flex items-center gap-1 px-3 py-1 border border-[#333] text-xs text-[#888]">
                  <Tag size={10} /> {t}
                </span>
              ))}
            </div>
          )}

          <div className="mt-16 p-8 border-2 border-[#333] text-center">
            <h3 className="text-2xl font-black mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Ready to capture your best ideas?
            </h3>
            <p className="text-[#888] mb-6">Join VAULT and start organizing your thoughts with AI.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-[#FF3B30] text-white font-bold px-8 py-3 border-2 border-[#FF3B30] hover:bg-transparent hover:text-[#FF3B30] transition-all text-sm tracking-wider uppercase"
            >
              Get Started Free
            </button>
          </div>
        </article>

        <style>{`
          .prose-vault h2 { font-size: 1.5rem; font-weight: 900; margin: 2rem 0 1rem; font-family: 'Space Grotesk', sans-serif; letter-spacing: -0.02em; }
          .prose-vault h3 { font-size: 1.25rem; font-weight: 700; margin: 1.5rem 0 0.75rem; font-family: 'Space Grotesk', sans-serif; }
          .prose-vault p { color: #A0A0A0; line-height: 1.8; margin-bottom: 1.25rem; font-size: 1rem; }
          .prose-vault ul, .prose-vault ol { color: #A0A0A0; padding-left: 1.5rem; margin-bottom: 1.25rem; }
          .prose-vault li { margin-bottom: 0.5rem; line-height: 1.7; }
          .prose-vault strong { color: #F0F0EE; }
          .prose-vault em { color: #888; }
          .prose-vault a { color: #007AFF; text-decoration: underline; }
        `}</style>
      </div>
    );
  }

  // Blog List View
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0EE]">
      <nav className="border-b border-[#222] bg-[#0A0A0A]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="text-xl font-black tracking-[-0.05em]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            VAULT
          </button>
          <div className="flex gap-4 text-sm text-[#888]">
            <button onClick={() => navigate('/blog')} className="text-white">Blog</button>
            <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Sign In</button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16" data-testid="blog-list">
        <h1 className="text-3xl sm:text-5xl font-black tracking-[-0.03em] mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Blog
        </h1>
        <p className="text-[#888] text-lg mb-12">Thoughts on ideas, productivity, and building better.</p>

        {posts.length === 0 ? (
          <div className="border-2 border-[#333] p-12 text-center">
            <p className="text-[#888] text-lg">No posts yet. Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-px bg-[#222]">
            {posts.map((post) => (
              <button
                key={post._id}
                onClick={() => navigate(`/blog/${post.slug}`)}
                data-testid={`blog-card-${post.slug}`}
                className="w-full bg-[#0A0A0A] p-8 text-left hover:bg-[#111] transition-colors group flex items-start justify-between gap-4"
              >
                <div className="flex-1">
                  <h2 className="text-xl sm:text-2xl font-black mb-2 group-hover:text-[#FF3B30] transition-colors tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {post.title}
                  </h2>
                  <p className="text-sm text-[#888] mb-3 line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-[#555]">
                    <span>{post.created_at ? new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
                    <span>{post.views || 0} views</span>
                    {post.tags?.slice(0, 3).map((t, i) => (
                      <span key={i} className="px-2 py-0.5 border border-[#333] text-[10px]">{t}</span>
                    ))}
                  </div>
                </div>
                <ChevronRight size={20} className="text-[#555] group-hover:text-[#FF3B30] transition-colors mt-2 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}

        <div className="mt-16 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-[#888] hover:text-white transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
